// ReportViewer — uniform shell that every report inside Admin → Reports
// renders into. A report config supplies:
//   - title / subtitle / icon / accent colour
//   - filter spec (date range, optional extra fields)
//   - fetcher (url builder that takes the filter values and returns a URL)
//   - rowsKey, summary block builder, table columns, optional chart
// The shell does the rest: filter strip, summary cards, chart, table,
// and four export buttons (Excel / PDF / Word / CSV).

import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, TextField, Button, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, CircularProgress, Alert, Tooltip, Divider, alpha,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  DescriptionOutlined as WordIcon,
  TableChartOutlined as ExcelIcon,
  PictureAsPdfOutlined as PdfIcon,
  DataObjectOutlined as CsvIcon,
} from '@mui/icons-material';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import {
  Document, Packer, Paragraph, Table as DocxTable, TableRow as DocxRow,
  TableCell as DocxCell, HeadingLevel, TextRun, WidthType,
} from 'docx';

// ─── Tiny helpers ────────────────────────────────────────────────────
const todayIso = () => new Date().toISOString().slice(0, 10);
const fmtMoney = (n) => `GH₵${Number(n || 0).toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNumber = (n) => Number(n || 0).toLocaleString('en-GH');

// Trigger a browser download from a Blob.
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Build a 2-D array from rows + columns — used by every export path so
// the contents of Excel / PDF / Word / CSV stay in sync.
function buildExportMatrix(rows, columns) {
  const header = columns.map((c) => c.label);
  const body = rows.map((r) => columns.map((c) => {
    const v = c.value ? c.value(r) : r[c.key];
    return v ?? '';
  }));
  return { header, body };
}

// ─── Filter strip ────────────────────────────────────────────────────
// Default filter shape: { start, end } date range. A config can override
// by supplying its own `filterFields`. Each field renders as a date or
// text input. Keep this dumb — anything fancy belongs in the config.
function FilterStrip({ filters, setFilters, fields, onApply, loading }) {
  return (
    <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 2 }}>
      {fields.map((f) => (
        <TextField
          key={f.name}
          size="small"
          type={f.type || 'date'}
          label={f.label}
          value={filters[f.name] || ''}
          onChange={(e) => setFilters((p) => ({ ...p, [f.name]: e.target.value }))}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 160 }}
        />
      ))}
      <Button
        variant="contained" size="small" onClick={onApply} disabled={loading}
        sx={{ textTransform: 'none', fontWeight: 700, ml: 'auto' }}
      >
        {loading ? 'Loading…' : 'Apply'}
      </Button>
    </Stack>
  );
}

// ─── Summary cards ───────────────────────────────────────────────────
// Config supplies a list of { label, value, color? } via summaryCards().
function SummaryCards({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
      {items.map((it, i) => (
        <Card
          key={i}
          sx={{
            flex: '1 1 160px', minWidth: 160,
            border: '1px solid', borderColor: 'divider', boxShadow: 'none',
          }}
        >
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary" sx={{
              fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', display: 'block',
            }}>
              {it.label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: it.color || 'text.primary', lineHeight: 1.2, mt: 0.25 }}>
              {it.value}
            </Typography>
            {it.sub && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                {it.sub}
              </Typography>
            )}
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

// ─── Inline bar chart ────────────────────────────────────────────────
// Pure CSS so we don't pull in Recharts or Chart.js. Good enough for
// "show me a quick shape" — anything more advanced is a follow-up.
function MiniBarChart({ data, color = '#0e926a', valueFormatter }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map((d) => Number(d.value) || 0), 1);
  const fmt = valueFormatter || fmtNumber;
  return (
    <Stack direction="row" alignItems="flex-end" spacing={0.5} sx={{ height: 140 }}>
      {data.map((d, i) => {
        const h = (Number(d.value) / max) * 100;
        return (
          <Tooltip key={i} arrow title={`${d.label}: ${fmt(d.value)}`}>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{
                width: '100%', height: `${Math.max(h, 1)}%`,
                bgcolor: color, borderRadius: '3px 3px 0 0',
                transition: 'height 250ms ease',
                minHeight: 2,
              }} />
              <Typography variant="caption" sx={{ fontSize: '0.62rem', color: 'text.secondary' }}>
                {d.label}
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Stack>
  );
}

// Inline SVG line chart — same data shape as MiniBarChart.
function MiniLineChart({ data, color = '#0e926a', valueFormatter }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data.map((d) => Number(d.value) || 0), 1);
  const W = 600, H = 140, pad = 16;
  const stepX = (W - pad * 2) / (data.length - 1);
  const fmt = valueFormatter || fmtNumber;
  const points = data.map((d, i) => {
    const x = pad + i * stepX;
    const y = H - pad - ((Number(d.value) || 0) / max) * (H - pad * 2);
    return [x, y];
  });
  const path = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  return (
    <Box sx={{ width: '100%', overflow: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none">
        <path d={path} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
        {points.map(([x, y], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r={2.5} fill={color} />
            <title>{`${data[i].label}: ${fmt(data[i].value)}`}</title>
          </g>
        ))}
      </svg>
      <Stack direction="row" spacing={0} sx={{ mt: -1 }}>
        {data.map((d, i) => (
          <Typography key={i} variant="caption" sx={{
            flex: 1, textAlign: 'center', fontSize: '0.6rem', color: 'text.secondary',
          }}>
            {d.label}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

// Card wrapper for any chart — gives it a title and a divider so the
// analysis page reads cleanly when several charts are stacked.
function ChartCard({ title, children, height }) {
  return (
    <Card sx={{ border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 1.5 }}>
      <CardContent sx={{ '&:last-child': { pb: 1.5 } }}>
        {title && (
          <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: 0.6, textTransform: 'uppercase', color: 'text.secondary', display: 'block', mb: 1 }}>
            {title}
          </Typography>
        )}
        <Box sx={{ height: height || 'auto' }}>
          {children}
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Exporters ───────────────────────────────────────────────────────
// Excel: SheetJS — generates a .xlsx with summary + body table.
function exportExcel(title, summaryItems, header, body, filename) {
  const summaryRows = (summaryItems || []).map((it) => [it.label, it.value]);
  const sheetData = [
    [title], [],
    ...(summaryRows.length ? [['Summary'], ...summaryRows, []] : []),
    ['Details'],
    header,
    ...body,
  ];
  const ws = XLSX.utils.aoa_to_sheet(sheetData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Report');
  XLSX.writeFile(wb, filename);
}

// PDF: jsPDF — landscape A4, title + summary + table. Lightweight enough
// without jspdf-autotable; we just lay out cells with fixed widths.
function exportPDF(title, summaryItems, header, body, filename) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  let y = 12;
  doc.setFontSize(14); doc.setFont('helvetica', 'bold');
  doc.text(title, 14, y);
  y += 8;
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text(`Generated ${new Date().toLocaleString()}`, 14, y);
  y += 8;
  if (summaryItems && summaryItems.length) {
    doc.setFont('helvetica', 'bold'); doc.text('Summary', 14, y); y += 5;
    doc.setFont('helvetica', 'normal');
    for (const it of summaryItems) {
      doc.text(`${it.label}: ${it.value}`, 14, y);
      y += 5;
      if (y > 195) { doc.addPage(); y = 12; }
    }
    y += 3;
  }
  // Table — auto-width per column, capped at 35 mm wide.
  const pageWidth = 297 - 28;
  const colWidth = Math.min(35, pageWidth / Math.max(header.length, 1));
  doc.setFont('helvetica', 'bold');
  header.forEach((h, i) => doc.text(String(h), 14 + i * colWidth, y));
  y += 5;
  doc.setFont('helvetica', 'normal');
  for (const row of body) {
    row.forEach((cell, i) => doc.text(String(cell ?? ''), 14 + i * colWidth, y));
    y += 5;
    if (y > 200) { doc.addPage(); y = 12; }
  }
  doc.save(filename);
}

// CSV: vanilla string with proper quote escaping.
function exportCSV(header, body, filename) {
  const esc = (v) => {
    const s = String(v ?? '');
    if (s.includes('"') || s.includes(',') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines = [header.map(esc).join(',')];
  for (const row of body) lines.push(row.map(esc).join(','));
  downloadBlob(new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' }), filename);
}

// Word: docx library — generates a .docx with title + table.
async function exportWord(title, summaryItems, header, body, filename) {
  const headerRow = new DocxRow({
    children: header.map((h) => new DocxCell({
      children: [new Paragraph({ children: [new TextRun({ text: String(h), bold: true })] })],
      width: { size: Math.floor(10000 / header.length), type: WidthType.DXA },
    })),
  });
  const bodyRows = body.map((row) => new DocxRow({
    children: row.map((cell) => new DocxCell({
      children: [new Paragraph({ children: [new TextRun(String(cell ?? ''))] })],
      width: { size: Math.floor(10000 / header.length), type: WidthType.DXA },
    })),
  }));
  const summaryParas = (summaryItems || []).map((it) =>
    new Paragraph({ children: [new TextRun({ text: `${it.label}: `, bold: true }), new TextRun(String(it.value))] })
  );
  const doc = new Document({
    sections: [{
      children: [
        new Paragraph({ children: [new TextRun({ text: title, bold: true, size: 32 })], heading: HeadingLevel.HEADING_1 }),
        new Paragraph({ children: [new TextRun({ text: `Generated ${new Date().toLocaleString()}`, size: 18, italics: true })] }),
        new Paragraph({ children: [new TextRun('')] }),
        ...(summaryParas.length ? [new Paragraph({ children: [new TextRun({ text: 'Summary', bold: true, size: 24 })] }), ...summaryParas, new Paragraph({ children: [new TextRun('')] })] : []),
        new Paragraph({ children: [new TextRun({ text: 'Details', bold: true, size: 24 })] }),
        new DocxTable({ rows: [headerRow, ...bodyRows], width: { size: 100, type: WidthType.PERCENTAGE } }),
      ],
    }],
  });
  const blob = await Packer.toBlob(doc);
  downloadBlob(blob, filename);
}

// ─── Public component ────────────────────────────────────────────────
//   config: {
//     id, title, subtitle, accent (hex),
//     filterFields:    [{ name, label, type? }]   default = start + end
//     defaultFilters:  { start, end, ... }        default = today twice
//     fetch(filters):  () => URL                  required
//     summaryCards(summary, rows):  -> [{ label, value, color?, sub? }]
//     chart(rows):                  -> [{ label, value }] | null
//     columns:                       [{ key, label, value?(row), align? }]
//     fileBase:                       used as filename prefix
//   }
export default function ReportViewer({ config }) {
  const initFilters = useMemo(() => {
    const t = todayIso();
    const fields = config.filterFields || [
      { name: 'start', label: 'From',  type: 'date' },
      { name: 'end',   label: 'To',    type: 'date' },
    ];
    const base = {};
    for (const f of fields) base[f.name] = '';
    return { ...base, ...(config.defaultFilters || { start: t, end: t }) };
  }, [config]);

  const [filters, setFilters] = useState(initFilters);
  const [summary, setSummary] = useState({});
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const url = config.fetch(filters);
      const res = await axios.get(url);
      if (res.data?.success) {
        setSummary(res.data.summary || {});
        setRows(Array.isArray(res.data.rows) ? res.data.rows : []);
      } else {
        setError(res.data?.message || 'Failed to load report.');
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Network error.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, JSON.stringify(filters)]);

  // Load once on mount with the default filters.
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const summaryItems = useMemo(
    () => config.summaryCards ? config.summaryCards(summary, rows) : [],
    [config, summary, rows]
  );
  const chartData = useMemo(
    () => config.chart ? config.chart(rows, summary) : null,
    [config, rows, summary]
  );
  // Multi-chart support — a config can supply an array of charts via
  // `charts(rows, summary)` for the analysis-page layout. Each entry is
  // { title, type ('bar'|'line'), data, color?, format? }.
  const chartsList = useMemo(
    () => config.charts ? (config.charts(rows, summary) || []) : null,
    [config, rows, summary]
  );
  const columns = config.columns || [];
  const { header, body } = useMemo(() => buildExportMatrix(rows, columns), [rows, columns]);

  const filename = (ext) => {
    const base = config.fileBase || config.id || 'report';
    const stamp = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19);
    return `${base}-${stamp}.${ext}`;
  };

  const accent = config.accent || '#1a6b8a';
  const filterFields = config.filterFields || [
    { name: 'start', label: 'From',  type: 'date' },
    { name: 'end',   label: 'To',    type: 'date' },
  ];

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 1.75,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: alpha(accent, 0.18), color: accent,
          border: `1px solid ${alpha(accent, 0.3)}`,
        }}>
          {config.icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>{config.title}</Typography>
          {config.subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
              {config.subtitle}
            </Typography>
          )}
        </Box>
        <Tooltip arrow title="Reload">
          <IconButton size="small" onClick={load} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <FilterStrip filters={filters} setFilters={setFilters} fields={filterFields} onApply={load} loading={loading} />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && rows.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={28} /></Box>
      ) : (
        <>
          <SummaryCards items={summaryItems} />

          {/* Single bar chart (legacy `chart` config) wrapped in a
              titled card so it visually matches the multi-chart layout. */}
          {chartData && (
            <ChartCard title="Trend">
              <MiniBarChart data={chartData} color={accent} />
            </ChartCard>
          )}

          {/* Multi-chart analysis layout. Each chart sits in its own
              titled card; bars or lines depending on chart.type. */}
          {chartsList && chartsList.length > 0 && (
            <Box>
              {chartsList.map((c, i) => (
                <ChartCard key={i} title={c.title} height={c.height}>
                  {c.type === 'line'
                    ? <MiniLineChart data={c.data} color={c.color || accent} valueFormatter={c.format} />
                    : <MiniBarChart  data={c.data} color={c.color || accent} valueFormatter={c.format} />}
                </ChartCard>
              ))}
            </Box>
          )}

          {/* Export buttons. All four walk the same header/body matrix
              built once from the visible columns above, so an XLSX, PDF,
              Word and CSV from the same view are guaranteed identical. */}
          <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', useFlexGap: true }}>
            <Button size="small" variant="outlined" startIcon={<ExcelIcon />}
              onClick={() => exportExcel(config.title, summaryItems, header, body, filename('xlsx'))}
              sx={{ textTransform: 'none' }}>Excel</Button>
            <Button size="small" variant="outlined" startIcon={<PdfIcon />}
              onClick={() => exportPDF(config.title, summaryItems, header, body, filename('pdf'))}
              sx={{ textTransform: 'none' }}>PDF</Button>
            <Button size="small" variant="outlined" startIcon={<WordIcon />}
              onClick={() => exportWord(config.title, summaryItems, header, body, filename('docx'))}
              sx={{ textTransform: 'none' }}>Word</Button>
            <Button size="small" variant="outlined" startIcon={<CsvIcon />}
              onClick={() => exportCSV(header, body, filename('csv'))}
              sx={{ textTransform: 'none' }}>CSV</Button>
            <Chip
              size="small"
              label={`${rows.length} row${rows.length === 1 ? '' : 's'}`}
              sx={{ ml: 'auto', alignSelf: 'center', fontWeight: 700 }}
            />
          </Stack>

          {/* Data table — skipped entirely when the config has no
              columns (analysis-only reports use just summary + charts). */}
          {columns.length === 0 ? null : rows.length === 0 ? (
            <Alert severity="info">No data for the selected filters.</Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(accent, 0.08) }}>
                    {columns.map((c) => (
                      <TableCell key={c.key} align={c.align || 'left'} sx={{ fontWeight: 800 }}>
                        {c.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r, i) => {
                    // Reversed rows render with a strike-through and a
                    // muted colour so the tech can scan the list and
                    // immediately see what's been refunded. The flag is
                    // set by the config's `rowProps(row)` hook.
                    const reversed = config.isReversed ? config.isReversed(r) : false;
                    return (
                      <TableRow
                        key={i}
                        hover
                        sx={reversed ? {
                          textDecoration: 'line-through',
                          color: 'text.disabled',
                          '& td': { color: 'text.disabled' },
                        } : undefined}
                      >
                        {columns.map((c) => (
                          <TableCell key={c.key} align={c.align || 'left'}>
                            {c.value ? c.value(r) : (r[c.key] ?? '')}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </Box>
  );
}

// Re-export the formatters so report configs can stay tidy.
export { fmtMoney, fmtNumber };
